import * as Sentry from '@sentry/nextjs';
import { PersonCheckIcon, PersonOutlineIcon } from '@c/icons';
import ErrorToast from '@c/toasts/ErrorToast';
import Button, { ButtonProps } from '@ui/Button';
import { followSeller, unfollowSeller } from '@util/firestore/users';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { useEffect, useRef, useState } from 'react';

interface FollowSellerButtonProps {
  sellerId: string;
  // Optional size pass-through so the same button can sit in a compact context
  // (the PDP SellerCard, beside "Message Seller") as well as its original
  // storefront home. Typed from Button's own props so they can never drift.
  // Defaults reproduce the storefront behavior exactly, so every existing call
  // site is unchanged.
  height?: ButtonProps['height'];
  width?: ButtonProps['width'];
}

/**
 * Follow/unfollow a seller from their storefront (GB #769).
 *
 * Following is stored as the seller's uid in the signed-in user's `following`
 * array; the notification fan-out for new listings already reads that array
 * server-side, so this button is the only missing piece of the feature.
 *
 * The button paints the new state immediately and reverts if the write fails,
 * rather than leaving the user looking at a state the database never accepted.
 */
const FollowSellerButton = ({
  sellerId,
  height,
  width = 'fixed',
}: FollowSellerButtonProps) => {
  const { user, userDoc, isLoadingUserDoc } = useAuth();
  const { showToast } = useToastContext();

  const followedInDoc = !!userDoc?.following?.includes(sellerId);
  // Holds the state we just wrote until the refreshed user doc agrees with it.
  // Deriving from `userDoc` alone would flash the old label for as long as the
  // refetch takes; mirroring into state alone would let a stale doc flip the
  // button back mid-write.
  //
  // The value is tagged with the seller it was written for. Next.js reuses this
  // component across /shop-seller/[username] navigations, so an untagged value
  // would follow the viewer to the next seller's page, render a stranger as
  // already-followed, and turn the next click into an unfollow of someone they
  // never followed. Comparing the tag during render (rather than clearing it in
  // an effect) means the wrong label is never painted, not even for one frame.
  // Optimistic entries, keyed by seller and held until the user document
  // agrees with them (or the write fails). Keying by seller is what lets the
  // viewer move between storefronts mid-write without either inheriting
  // another seller's state or losing their own: an earlier version cleared the
  // entry on navigation, which meant returning to a seller whose write was
  // still open showed a stale label that clicks could not fix, because the
  // in-flight guard was still holding.
  const [pending, setPending] = useState<
    Record<string, { following: boolean; token: number }>
  >({});

  // Every write gets its own token, and in-flight writes are tracked as a list
  // rather than "the most recent seller". Firestore keeps writes queued
  // indefinitely while the device is offline, so several can genuinely overlap
  // once the viewer moves between storefronts. Holding only the latest seller
  // string meant a request that settled late spoke for whatever was current.
  const [inFlight, setInFlight] = useState<
    { sellerId: string; token: number }[]
  >([]);
  const nextToken = useRef(0);

  const isFollowing = pending[sellerId]?.following ?? followedInDoc;
  const isSaving = inFlight.some((r) => r.sellerId === sellerId);

  // The signed-in account can change without this page unmounting — the admin
  // impersonation flow on this storefront calls signInWithToken, and a
  // cross-tab sign-in reaches the same listener. Optimistic entries belong to
  // the account that made them, so none of them may outlive it: otherwise the
  // new account inherits "Following" for a seller it never followed, and any
  // still-queued write leaves its button inert.
  const viewerUid = userDoc?.uid;
  useEffect(() => {
    setPending({});
    setInFlight([]);
  }, [viewerUid]);

  useEffect(() => {
    // Retire every entry the document has caught up with, not just the seller
    // on screen — `following` carries all of them, so they can all be checked.
    setPending((current) => {
      const remaining = Object.fromEntries(
        Object.entries(current).filter(
          ([sid, entry]) =>
            entry.following !== !!userDoc?.following?.includes(sid)
        )
      );
      // Same reference when nothing retired, or this would loop forever.
      return Object.keys(remaining).length === Object.keys(current).length
        ? current
        : remaining;
    });
  }, [userDoc]);

  // Until auth settles, we cannot tell a signed-in visitor from a guest.
  // `user` is undefined until Firebase Auth reports in, and isLoadingUserDoc
  // covers the gap between "signed in" and their user document arriving.
  // Rendering in that window would show a Follow button on the seller's own
  // storefront and would answer a signed-in visitor's click with the login
  // popup. A confirmed guest is `user === null`, which is settled and renders.
  if (user === undefined || isLoadingUserDoc) return null;

  // Following yourself does nothing useful, so the button is not offered.
  if (userDoc && userDoc.uid === sellerId) return null;

  const onClick = async () => {
    if (!userDoc) {
      window.open(
        '/login',
        'Login/Register',
        'width=600,height=700,top=0,left=0'
      );
      return;
    }

    if (isSaving) return;

    const next = !isFollowing;
    const token = ++nextToken.current;
    setPending((current) => ({
      ...current,
      [sellerId]: { following: next, token },
    }));
    setInFlight((current) => [...current, { sellerId, token }]);

    try {
      if (next) await followSeller(userDoc.uid, sellerId);
      else await unfollowSeller(userDoc.uid, sellerId);

      // No cache invalidation here on purpose. AuthContext keeps `userDoc` in
      // component state fed by listenToUserDocument's onSnapshot, not in the
      // react-query cache, so the follow state refreshes itself when Firestore
      // echoes the write back. An invalidate of ['user'] would not help it, and
      // react-query matches by key prefix, so it would additionally refetch the
      // ['user', reviewer_id] query that ReviewCard runs for every review shown
      // on this page — a pile of Firestore reads per click, for nothing.
    } catch (err) {
      // Roll back only the entry this request created. Matching on sellerId
      // alone was not enough: a later write for the same seller would be
      // rolled back by this earlier one's failure.
      setPending((current) =>
        current[sellerId]?.token === token
          ? Object.fromEntries(
              Object.entries(current).filter(([sid]) => sid !== sellerId)
            )
          : current
      );
      // A rejected write here is almost always a Firestore rules change that
      // stopped allowing `following` on the user doc. Without this the failure
      // would be invisible outside the toast the user dismisses.
      Sentry.captureException(err, {
        tags: { source: 'follow-seller-button' },
        extra: { sellerId, intent: next ? 'follow' : 'unfollow' },
      });
      showToast(
        <ErrorToast
          message={
            next
              ? 'Could not follow this seller. Please try again.'
              : 'Could not unfollow this seller. Please try again.'
          }
        />
      );
    } finally {
      // Release only this request. Any other write still open — including a
      // later one for this same seller — keeps its own guard.
      setInFlight((current) => current.filter((r) => r.token !== token));
    }
  };

  // Button only forwards `title`/`ariaText` on its Link branch, so a tooltip
  // passed here would never render. The visible label carries the state.
  //
  // width="fixed" (w-[15rem]) rather than "fluid": it keeps this button the
  // same width as the Message button it sits beside, and it keeps the button
  // from resizing under the cursor when the label grows from "Follow" to
  // "Following" on click.
  return (
    <Button
      type={isFollowing ? 'tertiary' : 'secondary'}
      text={isFollowing ? 'Following' : 'Follow'}
      width={width}
      height={height}
      leadingIcon={isFollowing ? <PersonCheckIcon /> : <PersonOutlineIcon />}
      onClick={() => {
        void onClick();
      }}
    />
  );
};

export default FollowSellerButton;
